import React, { useState, useEffect } from 'react'
import * as api from '../../services/api.js'
import FilterBar from '../../components/FilterBar.jsx'

const PLANT_CARE_GUIDES = [
  {
    id: 'succulents',
    name: 'Succulents & Cacti',
    icon: '🌵',
    desc: 'Low-maintenance plants that store water in their leaves and stems. Perfect for bright, dry spots and forgetful waterers.',
    facts: [
      { icon: '💧', text: 'Water every 2–4 weeks; let soil dry completely between waterings.' },
      { icon: '☀️', text: 'Need at least 6 hours of bright light daily — south or east window.' },
      { icon: '🌍', text: 'Use fast-draining cactus mix; never let roots sit in water.' },
      { icon: '🌡️', text: 'Prefer temps 15–28°C; most are not frost-hardy.' },
      { icon: '🌱', text: 'Propagate by leaf or stem cuttings — let cut ends callous 1–2 days first.' },
    ],
  },
  {
    id: 'herbs',
    name: 'Kitchen Herbs',
    icon: '🌿',
    desc: 'Fragrant and useful plants that thrive on a sunny windowsill. Most prefer good drainage and regular harvesting.',
    facts: [
      { icon: '💧', text: 'Water when the top centimetre of soil feels dry — herbs hate soggy roots.' },
      { icon: '☀️', text: 'Most need 6+ hours of sun; basil and mint tolerate partial shade.' },
      { icon: '✂️', text: 'Pinch off flower buds to keep plants bushy and productive.' },
      { icon: '🌍', text: 'Use gritty, well-draining potting mix with good-sized drainage holes.' },
      { icon: '🌱', text: 'Mint and chives spread readily — grow in separate pots to control them.' },
    ],
  },
  {
    id: 'tropicals',
    name: 'Tropical Houseplants',
    icon: '🌺',
    desc: 'Monstera, pothos, peace lilies and friends — lush, easy-going plants that bring the jungle indoors.',
    facts: [
      { icon: '💧', text: 'Water when the top 2–3 cm of soil are dry; reduce in winter.' },
      { icon: '☀️', text: 'Bright indirect light is ideal; direct sun scorches soft leaves.' },
      { icon: '💦', text: 'Mist leaves or use a pebble tray to boost humidity.' },
      { icon: '🌍', text: 'Repot in spring when roots circle the bottom of the pot.' },
      { icon: '🌱', text: 'Propagate pothos and philodendron easily in water — roots in 2–3 weeks.' },
    ],
  },
  {
    id: 'flowering',
    name: 'Flowering Plants',
    icon: '🌸',
    desc: 'Roses, geraniums, begonias and more — plants grown for their colourful blooms and cheerful presence.',
    facts: [
      { icon: '💧', text: 'Keep soil consistently moist during flowering; avoid wetting petals.' },
      { icon: '☀️', text: 'Most flowering plants need 6+ hours of light to bloom well.' },
      { icon: '✂️', text: 'Deadhead spent flowers regularly to encourage continuous blooming.' },
      { icon: '🌱', text: 'Feed with high-potassium fertiliser every 2 weeks during flowering season.' },
      { icon: '🍂', text: 'Cut back after flowering and allow a dormant rest period.' },
    ],
  },
  {
    id: 'vegetables',
    name: 'Vegetables & Edibles',
    icon: '🥬',
    desc: 'Growing your own food — from tomatoes to lettuce — is one of the most rewarding gardening experiences.',
    facts: [
      { icon: '☀️', text: 'Most vegetables need 8+ hours of direct sun for good yields.' },
      { icon: '💧', text: 'Water deeply and consistently — shallow watering causes shallow roots.' },
      { icon: '🌍', text: 'Enrich soil with compost before planting and feed every 2–4 weeks.' },
      { icon: '✂️', text: 'Harvest regularly to encourage continuous production.' },
      { icon: '🌱', text: 'Rotate crops each year to prevent soil depletion and disease build-up.' },
    ],
  },
  {
    id: 'bulbs',
    name: 'Bulbs & Seasonal Colour',
    icon: '🌷',
    desc: 'Tulips, daffodils, hyacinths — planted in autumn to reward you with spring colour after a patient wait.',
    facts: [
      { icon: '🌍', text: 'Plant bulbs with the pointed end up, at 2–3× the bulb\'s depth.' },
      { icon: '💧', text: 'Water after planting, then let winter rain do the work.' },
      { icon: '☀️', text: 'Choose a sunny spot; most bulbs dislike waterlogged soil.' },
      { icon: '🍂', text: 'Let leaves die back naturally after flowering — they feed the bulb for next year.' },
      { icon: '🌱', text: 'Lift and divide overcrowded bulbs every 3–4 years for best flowering.' },
    ],
  },
]

const TIP_CATEGORIES = [
  {
    id: 'watering', label: 'Watering', icon: '💧',
    tips: [
      'Water when the top inch of soil feels dry — stick your finger in to check.',
      'Use room-temperature water to avoid shocking roots.',
      'Water slowly until it drains from the bottom holes.',
      'Reduce watering in winter when most plants are dormant.',
      'Mist tropical plants lightly to boost humidity.',
    ],
    note: 'Overwatering is the #1 killer of indoor plants. When in doubt, wait a day.',
  },
  {
    id: 'light', label: 'Light & Placement', icon: '☀️',
    tips: [
      'Most houseplants prefer bright indirect light — avoid harsh midday sun through glass.',
      'Rotate pots a quarter turn each week for even growth.',
      'South-facing windows offer the most light year-round.',
      'Use a grow light if your home lacks natural light — 12–14 hours for most herbs.',
      'Watch for leggy, stretched stems — a sign the plant needs more light.',
    ],
    note: 'Move plants gradually when changing their light exposure to prevent shock.',
  },
  {
    id: 'soil', label: 'Soil & Nutrients', icon: '🌍',
    tips: [
      'Use well-draining potting mix unless your plant specifically needs something else.',
      'Feed with balanced liquid fertiliser every 2–4 weeks during the growing season.',
      'Repot when roots circle the bottom or poke out of drainage holes.',
      'Cactus and succulent mix drains faster — great for drought-tolerant plants.',
      'Add perlite to standard mix to improve drainage and aeration.',
    ],
    note: 'Never fertilise a stressed or recently repotted plant — it can burn the roots.',
  },
  {
    id: 'pests', label: 'Pest Control', icon: '🐛',
    tips: [
      'Inspect new plants before bringing them home — check undersides of leaves.',
      'Wipe leaves with a damp cloth to remove dust and deter spider mites.',
      'Neem oil spray is a safe, natural treatment for most common pests.',
      'Sticky yellow traps catch fungus gnats and whiteflies early.',
      'Isolate any infested plant immediately to protect your others.',
    ],
    note: 'Catching pests early makes all the difference — check your plants weekly.',
  },
  {
    id: 'pruning', label: 'Pruning & Growth', icon: '✂️',
    tips: [
      'Pinch off growing tips to encourage bushier, fuller plants.',
      'Remove dead or yellowing leaves promptly — they drain energy.',
      'Use clean, sharp scissors to avoid tearing and spreading disease.',
      'Prune in spring before the main growing season for the best recovery.',
      'Deadhead flowering plants regularly to extend the blooming period.',
    ],
    note: 'Most plants can handle losing up to one-third of their foliage at once.',
  },
  {
    id: 'seasonal', label: 'Seasonal Care', icon: '🍂',
    tips: [
      'Reduce watering and feeding in autumn and winter as growth slows.',
      'Keep plants away from cold draughts and radiators in winter.',
      'Acclimatise outdoor plants gradually before bringing them inside for winter.',
      'Spring is the best time to repot, divide, and take cuttings.',
      'Increase humidity in winter — heated indoor air is very dry.',
    ],
    note: 'Most houseplants prefer temperatures between 15–24°C (59–75°F) year-round.',
  },
  {
    id: 'propagation', label: 'Propagation', icon: '🌿',
    tips: [
      'Stem cuttings in water work well for pothos, philodendron, and impatiens.',
      'Let cut ends dry (callous) for 1–2 days before potting succulents.',
      'Division is the easiest method for spider plants, peace lilies, and hostas.',
      'Use rooting hormone powder to speed up root formation on woody cuttings.',
      'Keep propagation cuttings in bright indirect light — not full sun.',
    ],
    note: 'Spring and early summer give cuttings the best chance of rooting successfully.',
  },
]

const SECTION_EMOJIS = ['🌿', '🌸', '🌻', '🌱', '🍃', '🌺', '🌾', '🍀', '🌼', '🌳', '🪴', '🌵', '🍄', '🌲', '🌙', '⭐']
const MY_TIPS_FILTER = 'my-tips'

function PlantGuideCard({ guide }) {
  const [open, setOpen] = useState(false)
  return (
    <div className="plant-guide-card">
      <button className="plant-guide-toggle" onClick={() => setOpen(o => !o)}>
        <span className="plant-guide-icon">{guide.icon}</span>
        <span className="plant-guide-name">{guide.name}</span>
        <span className="plant-guide-chevron">{open ? '▲' : '▼'}</span>
      </button>
      {open && (
        <div className="plant-guide-body">
          <p className="plant-guide-desc">{guide.desc}</p>
          <ul className="plant-guide-facts">
            {guide.facts.map((f, i) => (
              <li key={i} className="plant-guide-fact">
                <span className="plant-guide-fact-icon">{f.icon}</span>
                <span>{f.text}</span>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  )
}

export default function GardenCare() {
  const [myTips,         setMyTips]         = useState({})
  const [customSections, setCustomSections] = useState([])
  const [activeFilter,   setActiveFilter]   = useState('all')
  const [openTip,        setOpenTip]        = useState(null)
  const [expandedTips,   setExpandedTips]   = useState({})
  const [draftTip,       setDraftTip]       = useState({})
  const [showAddSection, setShowAddSection] = useState(false)
  const [newSectionName, setNewSectionName] = useState('')
  const [newSectionIcon, setNewSectionIcon] = useState('🌿')
  const [showAddTip,     setShowAddTip]     = useState({})

  useEffect(() => {
    api.getGardenTips().then(d => setMyTips(d || {}))
    api.getSetting('garden_sections_v1', '[]').then(v => {
      try { setCustomSections(JSON.parse(v) || []) } catch { /* keep default */ }
    })
  }, [])

  function saveMyTip(catId) {
    const text = (draftTip[catId] || '').trim()
    if (!text) return
    const existing = myTips[catId] || []
    const next = { ...myTips, [catId]: [...existing, { id: Date.now(), text }] }
    setMyTips(next)
    api.saveGardenTips(next)
    setDraftTip(prev => ({ ...prev, [catId]: '' }))
    setShowAddTip(prev => ({ ...prev, [catId]: false }))
  }

  function deleteMyTip(catId, tipId) {
    const updated = (myTips[catId] || []).filter(t => t.id !== tipId)
    const next = { ...myTips, [catId]: updated }
    setMyTips(next)
    api.saveGardenTips(next)
  }

  function addCustomSection() {
    const name = newSectionName.trim()
    if (!name) return
    const section = { id: `cs-${Date.now()}`, name, icon: newSectionIcon }
    const next = [...customSections, section]
    setCustomSections(next)
    api.saveSetting('garden_sections_v1', JSON.stringify(next))
    setNewSectionName('')
    setNewSectionIcon('🌿')
    setShowAddSection(false)
  }

  function deleteCustomSection(id) {
    const next = customSections.filter(s => s.id !== id)
    setCustomSections(next)
    api.saveSetting('garden_sections_v1', JSON.stringify(next))
    const { [id]: _, ...rest } = myTips
    setMyTips(rest)
    api.saveGardenTips(rest)
  }

  function exportTips() {
    const lines = []
    const allCats = [
      ...TIP_CATEGORIES.map(c => ({ id: c.id, label: c.label, icon: c.icon })),
      ...customSections.map(s => ({ id: s.id, label: s.name, icon: s.icon })),
    ]
    allCats.forEach(cat => {
      const tips = myTips[cat.id] || []
      if (!tips.length) return
      lines.push(`## ${cat.icon} ${cat.label}`)
      tips.forEach(t => lines.push(`- ${t.text}`))
      lines.push('')
    })
    if (!lines.length) return
    const blob = new Blob([lines.join('\n')], { type: 'text/plain' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = 'my-garden-tips.txt'
    a.click()
    URL.revokeObjectURL(url)
  }

  const allCategories = [
    ...TIP_CATEGORIES,
    ...customSections.map(s => ({ id: s.id, label: s.name, icon: s.icon, tips: [], note: null })),
  ]

  const categoriesWithMyTips = allCategories.filter(c => (myTips[c.id] || []).length > 0)
  const totalMyTips = Object.values(myTips).reduce((sum, arr) => sum + (arr || []).length, 0)

  const visibleCategories =
    activeFilter === 'all' ? allCategories
    : activeFilter === MY_TIPS_FILTER ? categoriesWithMyTips
    : allCategories.filter(c => c.id === activeFilter)

  return (
    <div>
      {/* ── Plant care guides ── */}
      <div style={{ marginBottom: '2rem' }}>
        <p style={{ fontSize: '0.8rem', color: 'var(--stone)', marginBottom: '0.75rem' }}>
          Quick care guides for common plant types.
        </p>
        {PLANT_CARE_GUIDES.map(g => <PlantGuideCard key={g.id} guide={g} />)}
      </div>

      {/* ── Tips section heading + actions ── */}
      <div style={{ marginBottom: '1rem' }}>
        <div style={{
          fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.1em',
          color: 'var(--stone)', marginBottom: '0.75rem',
        }}>
          Care Tips
        </div>
        <div className="learn-tab-actions">
          <button
            className="learn-action-btn"
            onClick={() => {
              setActiveFilter('all')
              setOpenTip(activeFilter === 'all' && openTip ? openTip : null)
              setShowAddTip(prev => ({ ...prev, [activeFilter]: !prev[activeFilter] }))
            }}
          >
            ✏️ Write a tip
          </button>
          <button className="learn-action-btn learn-action-btn--secondary" onClick={() => setShowAddSection(s => !s)}>
            + New section
          </button>
          {totalMyTips > 0 && (
            <button className="learn-action-btn learn-action-btn--secondary" onClick={exportTips}>
              ↓ Export my tips
            </button>
          )}
        </div>
      </div>

      {/* ── New section form ── */}
      {showAddSection && (
        <div className="add-section-form">
          <div style={{ fontSize: '0.8rem', fontWeight: 500, color: 'var(--ink)' }}>New tip section</div>
          <div className="section-emoji-row">
            {SECTION_EMOJIS.map(e => (
              <button
                key={e}
                className={`section-emoji-btn${newSectionIcon === e ? ' selected' : ''}`}
                onClick={() => setNewSectionIcon(e)}
              >
                {e}
              </button>
            ))}
          </div>
          <input
            type="text"
            value={newSectionName}
            onChange={ev => setNewSectionName(ev.target.value)}
            onKeyDown={e => e.key === 'Enter' && addCustomSection()}
            placeholder="Section name…"
          />
          <div style={{ display: 'flex', gap: '0.5rem' }}>
            <button className="learn-action-btn" onClick={addCustomSection} disabled={!newSectionName.trim()}>
              Add section
            </button>
            <button className="learn-action-btn learn-action-btn--secondary" onClick={() => setShowAddSection(false)}>
              Cancel
            </button>
          </div>
        </div>
      )}

      {/* ── Filter bar ── */}
      <FilterBar
        filters={[
          { key: 'all', label: 'All' },
          ...allCategories.map(c => ({ key: c.id, label: c.label, icon: c.icon })),
          { key: MY_TIPS_FILTER, label: `My Tips${totalMyTips > 0 ? ` (${totalMyTips})` : ''}`, icon: '✏️' },
        ]}
        active={activeFilter}
        onChange={setActiveFilter}
      />

      {activeFilter === MY_TIPS_FILTER && categoriesWithMyTips.length === 0 && (
        <p style={{ fontSize: '0.82rem', color: 'var(--stone)', fontStyle: 'italic', padding: '1rem 0 0.5rem' }}>
          No personal tips saved yet. Open any category and add your own!
        </p>
      )}

      {/* ── Tips accordion ── */}
      <div style={{ marginTop: '0.75rem' }}>
        {visibleCategories.map(cat => {
          const isBuiltIn   = TIP_CATEGORIES.some(c => c.id === cat.id)
          const catMyTips   = myTips[cat.id] || []
          const isOpen      = openTip === cat.id
          const draft       = draftTip[cat.id] || ''
          const tipExpanded = expandedTips[cat.id]
          const shownTips   = tipExpanded ? (cat.tips || []) : (cat.tips || []).slice(0, 3)

          return (
            <div key={cat.id} className="garden-tip-section">
              <button
                className="garden-tip-toggle"
                onClick={() => setOpenTip(isOpen ? null : cat.id)}
              >
                <span>{cat.icon}</span>
                <span className="garden-tip-toggle-label">
                  {cat.label}
                  {catMyTips.length > 0 && (
                    <span style={{
                      marginLeft: 6, fontSize: '0.6rem', background: 'var(--accent)',
                      color: 'white', borderRadius: 10, padding: '1px 6px', verticalAlign: 'middle',
                    }}>
                      {catMyTips.length} mine
                    </span>
                  )}
                </span>
                <span className="garden-tip-arrow">{isOpen ? '▲' : '▼'}</span>
              </button>

              {isOpen && (
                <div className="garden-tip-body">
                  {/* Built-in tips */}
                  {activeFilter !== MY_TIPS_FILTER && (cat.tips || []).length > 0 && (
                    <>
                      <ul className="garden-tip-list">
                        {shownTips.map((tip, i) => <li key={i}>{tip}</li>)}
                      </ul>
                      {(cat.tips || []).length > 3 && (
                        <button
                          className="wb-show-more"
                          style={{ marginBottom: '0.5rem' }}
                          onClick={() => setExpandedTips(prev => ({ ...prev, [cat.id]: !tipExpanded }))}
                        >
                          {tipExpanded ? 'Show less' : `Show ${(cat.tips || []).length - 3} more`}
                        </button>
                      )}
                      {cat.note && (
                        <>
                          <div className="garden-note-label">Tip</div>
                          <p style={{ fontSize: '0.8rem', color: 'var(--stone)', marginBottom: '0.75rem', lineHeight: 1.55 }}>
                            {cat.note}
                          </p>
                        </>
                      )}
                    </>
                  )}

                  {/* My personal tips */}
                  {catMyTips.length > 0 && (
                    <>
                      <div className="garden-note-label" style={{ marginBottom: 6 }}>My tips</div>
                      <ul style={{ listStyle: 'none', padding: 0, margin: '0 0 0.75rem' }}>
                        {catMyTips.map(t => (
                          <li key={t.id} style={{
                            display: 'flex', alignItems: 'flex-start', gap: 6,
                            fontSize: '0.84rem', color: 'var(--bark)', lineHeight: 1.7,
                            borderBottom: '1px solid rgba(0,0,0,0.06)', paddingBottom: 4, marginBottom: 4,
                          }}>
                            <span style={{ color: 'var(--accent)', flexShrink: 0 }}>✏️</span>
                            <span style={{ flex: 1 }}>{t.text}</span>
                            <button
                              onClick={() => deleteMyTip(cat.id, t.id)}
                              style={{
                                background: 'none', border: 'none', cursor: 'pointer',
                                color: 'var(--stone)', fontSize: '0.7rem', flexShrink: 0, padding: '0 2px',
                              }}
                              title="Remove tip"
                            >✕</button>
                          </li>
                        ))}
                      </ul>
                    </>
                  )}

                  {/* Custom section delete */}
                  {!isBuiltIn && (
                    <button
                      className="learn-action-btn learn-action-btn--secondary"
                      style={{ marginBottom: '0.75rem', fontSize: '0.74rem' }}
                      onClick={() => deleteCustomSection(cat.id)}
                    >
                      Delete this section
                    </button>
                  )}

                  {/* Add tip inline */}
                  {showAddTip[cat.id] ? (
                    <div className="add-tip-form">
                      <textarea
                        value={draft}
                        onChange={e => setDraftTip(prev => ({ ...prev, [cat.id]: e.target.value }))}
                        onKeyDown={e => e.key === 'Enter' && !e.shiftKey && (e.preventDefault(), saveMyTip(cat.id))}
                        placeholder="Write your tip here…"
                      />
                      <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                        <button
                          className="learn-action-btn"
                          style={{ fontSize: '0.78rem', padding: '0.38rem 0.75rem' }}
                          onClick={() => saveMyTip(cat.id)}
                          disabled={!draft.trim()}
                        >
                          Save
                        </button>
                        <button
                          className="learn-action-btn learn-action-btn--secondary"
                          style={{ fontSize: '0.78rem', padding: '0.38rem 0.75rem' }}
                          onClick={() => setShowAddTip(prev => ({ ...prev, [cat.id]: false }))}
                        >
                          Cancel
                        </button>
                      </div>
                    </div>
                  ) : (
                    <button
                      className="learn-action-btn learn-action-btn--secondary"
                      style={{ fontSize: '0.78rem' }}
                      onClick={() => setShowAddTip(prev => ({ ...prev, [cat.id]: true }))}
                    >
                      + Add my tip
                    </button>
                  )}
                </div>
              )}
            </div>
          )
        })}
      </div>
    </div>
  )
}
